import { Component, OnInit, Input, ChangeDetectorRef, ChangeDetectionStrategy, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ViewTreeNode } from 'src/app/engine/models/view.tree-node';
import { Store, Select } from '@ngxs/store';
import {
  FlyToObject,
  SetObjectLayerOrder,
  SetObjectDisplayName,
  CollapseViewTreeNode,
  ExpandViewTreeNode,
  SetLabelEditNode
} from 'src/app/engine/state/engine.actions';
import { EditState } from 'src/app/edit/state/edit.state';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { BdGraph } from 'src/lib/graph/graph.model';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EngineNodesState } from 'src/app/engine/state/nodes/engine.nodes.state';
import { TreeTheme } from '../tree/tree.component';
import { environment } from 'src/environments/environment';
import { GraphUtils } from 'src/lib/graph/graph-utils';
import { Router } from '@angular/router';
import { EnvironmentApplication } from 'src/environments/models/environment-application';
import { NodeActions } from 'src/app/engine/state/nodes/engine.nodes.actions';
import { EngineState } from 'src/app/engine/state/engine.state';
import { EngineService } from 'src/app/engine/engine.service';

@Component({
  selector: 'app-tree-nested-node',
  templateUrl: './tree-nested-node.component.html',
  styleUrls: ['./tree-nested-node.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})

export class TreeNestedNodeComponent implements OnInit, OnDestroy {
  treeTheme = TreeTheme;
  isLoft = environment.exportType === 'module';

  @Select(EngineNodesState.getCheckedNodeIds)
  checkedNodesIds$: Observable<string[]>;

  @Select(EditState.getLayerOrderMode)
  layerOrderMode$: Observable<boolean>;

  @Select(EngineState.getLabelEditNode)
  labelEditNode$: Observable<ViewTreeNode>;
  
  @Select(EditState.getShowTreeNodeIds)
  showTreeNodeIds$: Observable<boolean>;


  @Input()
  graph: BdGraph<ViewTreeNode>;

  @Input()
  public get node(): ViewTreeNode {
    return this._node;
  }
  public set node(value: ViewTreeNode) {
    this._node = value;
    
    this.sanitizedID = this.graphService.sanitizeNodeID(this.node.id);

    this.checked = this.isNodeChecked(this.node.id);
    const ids = this.node.id.split('-');
    if (ids.length === 2) {
      this.parentId = ids[0];
      this.shortId = ids[1];
    } else {
      this.shortId = ids[0];
      this.parentId = '';
    }
    this.shortId = this.shortId.replace(/_/g, ' ');

    if (this.nested) {
      this.hasChildren = GraphUtils.getSuccessors(this.graph, this.node.id).length > 0;
    }

    this.changeDetection.markForCheck();
  }

  private _node: ViewTreeNode;

  @Input()
  nested = true;

  @Input()
  theme: TreeTheme = TreeTheme.Dark;

  checked = false;
  hasChildren = false;
  editLabelMode = false;

  // DOM friendly version of node ID
  // nodeID may include pipe characters, which cause errors with querySelector
  sanitizedID = '';

  // Last piece of ID only, for loft users
  parentId = '';
  shortId = '';


  private ngUnsubscribe = new Subject();
  private isEditMode: boolean;

  constructor(
    private store: Store,
    private graphService: BdGraphService,
    private engineService: EngineService,
    private router: Router,
    private changeDetection: ChangeDetectorRef
  ) { }

  private isNodeChecked(id: string) {
    const nodes = this.store.selectSnapshot(EngineNodesState.getNodes);
    return nodes[id].checked;
  }

  private isNodeShown(id: string) {
    const nodes = this.store.selectSnapshot(EngineNodesState.getNodes);
    return nodes[id].shown;
  }

  ngOnInit() {
    this.checkedNodesIds$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(nodeIds => {
        this.checked = nodeIds.some((id) => id === this.node.id);
        this.changeDetection.markForCheck();
      });

    this.labelEditNode$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(node => {
        this.editLabelMode = node === this.node;
      });

    // set editing mode
    this.isEditMode = this.router.url.indexOf('/edit') >= 0;
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  handleExpandClick(node: ViewTreeNode, expanded: boolean) {
    (expanded) ?
      this.store.dispatch(new CollapseViewTreeNode(node)) :
      this.store.dispatch(new ExpandViewTreeNode(node));
  }

  handleNodeClick(event: MouseEvent, node: ViewTreeNode) {
    if (event.shiftKey) {
      this.checkNode(node);
    } else {
      this.setCheckedNodes(node);

      const flyToEnabled = this.isFlyToNodeEnabled(node);
      if (flyToEnabled) {
        this.store.dispatch(new FlyToObject(node.id));
      }

      this.handleExpandClick(node, !node.boolState.expanded);
    }
  }

  toggleVisible(node: ViewTreeNode, show?: boolean) {
    const shouldShow = show === undefined ? !this.isNodeShown(node.id) : show;
    this.store.dispatch(new NodeActions.UpdateNodesShown(shouldShow, [node.id]));
    this.changeDetection.markForCheck();
  }

  handleLayerOrder(event: Event, node: ViewTreeNode) {
    const eventTarget = event.target as HTMLInputElement;
    const layer = parseInt(eventTarget.value, 10);

    this.setLayerOrder(node, layer);
  }

  checkNode(node: ViewTreeNode, checked?: boolean) {
    const shouldCheck = checked === undefined ? !this.isNodeChecked(node.id) : checked;
    this.store.dispatch(new NodeActions.UpdateNodesChecked(shouldCheck, [node.id]));
  }


  setCheckedNodes(node: ViewTreeNode, checked?: boolean) {
    this.store.dispatch(new NodeActions.UpdateNodesChecked(false));
    const shouldCheck = checked === undefined ? !this.isNodeChecked(node.id) : checked;
    this.store.dispatch(new NodeActions.UpdateNodesChecked(shouldCheck, [node.id]));
  }

  setLayerOrder(node: ViewTreeNode, layer: number) {
    this.store.dispatch(new SetObjectLayerOrder([node], layer));
    this.changeDetection.markForCheck();
  }

  handleObjectLabel(event: Event) {
    const eventTarget = event.target as HTMLInputElement;

    if (event instanceof KeyboardEvent) {

      if (event.key.toLowerCase() === 'enter') {
        this.setLabel(eventTarget.value);
        this.setEditLabelMode(false);
      }

      if (event.key.toLowerCase() === 'escape') {
        this.setEditLabelMode(false);
      }

    } else if (event instanceof FocusEvent && event.type === 'blur') {
      this.setLabel(eventTarget.value);
      this.setEditLabelMode(false);
    }

  }

  setLabel(label: string) {
    this.node.label = label;
    this.store.dispatch(new SetObjectDisplayName(this.node.id, label));
  }

  setEditLabelMode (mode: boolean) { // Restrict to the /edit page in Loft
    if (environment.application === EnvironmentApplication.Loft && this.isEditMode) {
      this.store.dispatch(new SetLabelEditNode(mode ? this.node : null));
    }
  }

  private isFlyToNodeEnabled(node: ViewTreeNode): boolean {
    // fly to: when not editing
    if (!this.isEditMode) {
      return node.boolState.shown || this.engineService.getObjectLoaded(node.id);
    } else {
      return false;
    }
  }

}

